<template>
	<div :class="productSelector" :style="customProductStyles.main">
		<div
			v-if="product.popular"
			class="product--popular"
			:class="customProductClasses.popular"
			:style="customProductStyles.popular"
		>
			{{ t('product.most_popular') }}
		</div>
		<div class="product--left-side" :class="customProductClasses.leftSide" :style="customProductStyles.leftSide">
			<div
				class="product--label"
				v-html="product.text"
				:class="customProductClasses.title"
				:style="customProductStyles.title"
			></div>
			<div
				class="product--subtitle"
				v-html="product.subtitle"
				:class="customProductClasses.subtitle"
				:style="customProductStyles.subtitle"
			></div>
		</div>
		<div
			class="product--right-side"
			:class="[
				{ centered: product.text_over_badge && !product.badge && !product.badge_digits },
				customProductClasses.rightSide
			]"
			:style="customProductStyles.rightSide"
		>
			<div
				class="product--text-over-badge"
				v-if="product.text_over_badge"
				v-html="product.text_over_badge"
				:class="customProductClasses.textOverBadge"
				:style="customProductStyles.textOverBadge"
			></div>
			<div
				class="product--badge"
				v-if="!product.badge_only_digits && product.badge"
				:class="customProductClasses.badge"
				:style="customProductStyles.badge"
			>
				{{ product.badge }}
			</div>
			<div
				class="product--badge product--badge-only-digits"
				v-if="product.badge_only_digits && product.badge_digits"
				:class="customProductClasses.badge"
				:style="customProductStyles.badge"
			>
				{{ product.badge_digits }}
			</div>
		</div>
	</div>
</template>

<script>
import { computed } from '@vue/reactivity'
import { useI18n } from 'vue-i18n'

const SUPPORTED_THEMES = ['primary', 'white', 'black', 'ss_orange', 'ss_ice', 'ss_swimsuit']

export default {
	props: {
		product: {
			type: Object,
			required: true
		},
		isActive: {
			type: Boolean,
			required: true
		},
		theme: {
			type: String,
			default: 'primary',
			validation: (value) => {
				return SUPPORTED_THEMES.includes(value)
			}
		}
	},
	setup(props) {
		const { t, locale } = useI18n()

		const defaultStylingObject = {
			main: {},
			leftSide: {},
			rightSide: {},
			title: {},
			subtitle: {},
			textOverBadge: {},
			badge: {},
			popular: {}
		}

		const customProductClasses = computed(() => {
			return {
				...defaultStylingObject,
				...props.product.custom_classes
			}
		})

		const customProductStyles = computed(() => {
			return {
				...defaultStylingObject,
				...props.product.custom_styles
			}
		})

		const productSelector = computed(() => {
			const classList = ['product', props.theme]
			if (props.isActive) classList.push('active')
			if (props.product.popular) classList.push('popular')
			if (props.product.badge) classList.push('badge')
			if (customProductClasses.value.main) classList.push(customProductClasses.value.main)
			if (locale.value === 'de' || locale.value === 'fr') classList.push('dense')
			return classList
		})

		return {
			t,
			customProductClasses,
			customProductStyles,
			productSelector
		}
	}
}
</script>

<style scoped lang="scss">
$product-popular-padding-top: 1.1rem;

.product {
	background-color: var(--ios-product-background, #f5f5f7);
	padding: 0.72rem 1rem 0.72rem 1rem;
	margin-bottom: 0.72rem;
	border-radius: 0.7rem;
	border: 2px solid var(--ios-product-background, #f5f5f7);
	transition: border-color 0.3s, background-color 0.3s;
	position: relative;

	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: flex-start;

	&--left-side {
		flex: 2;
	}

	&--right-side {
		position: relative;
		margin-left: 0.5rem;
		&.centered {
			margin: auto;
		}
	}

	&--popular {
		position: absolute;
		left: -2px;
		font-size: 0.7rem;
		top: 0;
		transform: translateY(-50%);
		background-color: var(--ios-product-active-color, #7d8bf7);
		color: #fff;
		font-weight: 500;
		padding: 0.1rem 1rem;
		border-radius: 3px;
	}

	.old_price {
		margin-left: 4px;
	}

	&:last-child {
		margin-bottom: 0;
	}

	&--label {
		font-weight: bold;
		font-size: var(--ios-product-label-font-size, 1rem);
		line-height: 1.3;

		::v-deep i {
			font-style: normal;
			font-weight: 400;
		}

		::v-deep .inline-badge {
			background-color: #eca58f;
			color: #fff;
			font-size: 0.7rem;
			font-weight: bold;
			padding: 0.3rem 0.6rem;
			border-radius: 0.3rem;
			display: inline-flex;
			margin-left: 3px;
			letter-spacing: 0.92px;
			position: relative;
			bottom: 2px;
		}
	}

	&--subtitle {
		margin-top: 0.2rem;
		opacity: 0.9;

		::v-deep {
			s {
				opacity: 0.6;
			}

			small {
				font-size: 0.9rem;
				color: #6a6d82;
			}
		}
	}

	&--badge {
		min-width: 71px;
		margin-left: auto;
		background-color: #eca58f;
		color: #fff;
		font-size: 0.7rem;
		text-align: center;
		font-weight: bold;
		padding: 0.15rem 0.6rem;
		border-radius: 0.4rem;
		white-space: nowrap;

		&-only-digits {
			color: inherit;
			background: none;
			font-size: 1.8rem;
			padding: 0;
			border-radius: 0;

			&::before {
				content: '';
				position: absolute;
				top: 0;
				left: -10px;
				color: inherit;
				background-color: CurrentColor;
				width: 1px;
				height: 100%;
				opacity: 0.3;
			}
		}
	}

	&--text-over-badge {
		::v-deep b {
			font-size: 1.15rem;
		}
		font-size: 0.9rem;
		margin-bottom: 4px;

		&.big {
			::v-deep b {
				font-size: 1.3rem;
			}
		}
	}

	&.active {
		background-color: #fff;
		border-color: #eca58f;
	}

	&.popular {
		padding-top: $product-popular-padding-top;
	}

	&.primary {
		&.active {
			border-color: var(--ios-product-active-color, #7d8bf7);
			background-color: var(--ios-product-active-background, transparent);
		}
	}

	&.white {
		background-color: #fff;
	}

	&.black {
		text-align: center;
		background-color: #2c2c2c;
		border-color: #2c2c2c;
		color: #fff;
	}

	&.black & {
		&--subtitle {
			color: #08afe6;
		}
	}

	&.centered {
		text-align: center;
		border-color: #fff;
	}

	&.centered & {
		&--subtitle {
			color: #5667ff;
		}
	}

	&.orange & {
		&--label {
			color: #7e53d3;
		}

		&--subtitle {
			color: #7e53d3;
		}
	}

	&.ss_orange {
		background: rgba(221, 223, 240, 0.5);
		border-color: transparent;

		.product--badge {
			color: #ed6d08;
		}

		&.active {
			background: rgba(255, 211, 29, 0.2);
		}
	}

	&.ss_ice {
		background: rgba(221, 223, 240, 0.5);
		border-color: transparent;

		.product--badge {
			color: #f87d67;
		}

		&.active {
			background: rgba(248, 125, 103, 0.1);
		}
	}

	&.ss_swimsuit {
		background: rgba(221, 223, 240, 0.5);
		border-color: transparent;

		.product--badge {
			color: #f87d67;
		}

		&.active {
			background: rgba(248, 138, 103, 0.1);
		}
	}
}

[lang='ru'] {
	.product {
		&--label {
			line-height: 1.1;
		}
	}
}

[lang='fr'],
[lang='de'] {
	.product {
		padding: 0.75rem;

		&.popular {
			padding-top: $product-popular-padding-top * 1.1;
		}

		&--label {
			font-size: calc(var(--ios-product-label-font-size, 1rem) * 0.9);
		}
	}
}
</style>
